/** SSR/workers-safe nav bar color helpers (no DOM APIs). */

/** Fallback for color picker when no custom nav bar background is stored. */
export const NAV_BAR_BACKGROUND_PICKER_DEFAULT = '#ffffff';

/** CSS custom properties read by @pagayo/design storefront nav chrome. */
export const STOREFRONT_NAV_BAR_BG_VAR = '--storefront-nav-bar-bg';
export const STOREFRONT_SUBMENU_BG_VAR = '--storefront-submenu-bg';
export const STOREFRONT_MENU_HOVER_BG_VAR = '--storefront-menu-hover-bg';
export const STOREFRONT_SUBMENU_HOVER_BG_VAR = '--storefront-submenu-hover-bg';

export interface StorefrontNavBarColorSettings {
  navBarBackgroundColor?: unknown;
  submenuBackgroundColor?: unknown;
  menuHoverBackgroundColor?: unknown;
  submenuHoverBackgroundColor?: unknown;
}

export function normalizeNavBarBackgroundColor(value: unknown): string {
  if (typeof value !== 'string') return '';
  const hex = value.trim();
  if (!hex) return '';
  if (/^#([0-9a-fA-F]{3})$/.test(hex)) {
    return `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`.toLowerCase();
  }
  if (/^#([0-9a-fA-F]{6})$/.test(hex)) {
    return hex.toLowerCase();
  }
  return '';
}

/** Value for `<input type="color">` (never empty). */
export function navBarBackgroundColorForPicker(stored: unknown): string {
  return normalizeNavBarBackgroundColor(stored) || NAV_BAR_BACKGROUND_PICKER_DEFAULT;
}

function resolveCssVarStyle(stored: unknown, cssVar: string): Record<string, string> | undefined {
  const color = normalizeNavBarBackgroundColor(stored);
  if (!color) return undefined;
  return { [cssVar]: color };
}

/** Inline style when custom menu chrome colors are saved. */
export function resolveMenuChromeStyle(
  settings: StorefrontNavBarColorSettings = {},
): Record<string, string> | undefined {
  const style = {
    ...resolveCssVarStyle(settings.navBarBackgroundColor, STOREFRONT_NAV_BAR_BG_VAR),
    ...resolveCssVarStyle(settings.submenuBackgroundColor, STOREFRONT_SUBMENU_BG_VAR),
    ...resolveCssVarStyle(settings.menuHoverBackgroundColor, STOREFRONT_MENU_HOVER_BG_VAR),
    ...resolveCssVarStyle(settings.submenuHoverBackgroundColor, STOREFRONT_SUBMENU_HOVER_BG_VAR),
  };

  const navColor = normalizeNavBarBackgroundColor(settings.navBarBackgroundColor);
  if (navColor) {
    Object.assign(style, {
      backgroundColor: navColor,
    });
  }

  return Object.keys(style).length > 0 ? style : undefined;
}

/** @deprecated Use resolveMenuChromeStyle */
export function resolveNavBarBackgroundStyle(stored: unknown): Record<string, string> | undefined {
  return resolveMenuChromeStyle({ navBarBackgroundColor: stored });
}

/** @deprecated Use resolveMenuChromeStyle */
export function resolveSubmenuBackgroundStyle(stored: unknown): Record<string, string> | undefined {
  return resolveMenuChromeStyle({ submenuBackgroundColor: stored });
}

/** @deprecated Use resolveMenuChromeStyle */
export function resolveMenuBackgroundStyle(
  navStored: unknown,
  submenuStored?: unknown,
  menuHoverStored?: unknown,
  submenuHoverStored?: unknown,
): Record<string, string> | undefined {
  return resolveMenuChromeStyle({
    navBarBackgroundColor: navStored,
    submenuBackgroundColor: submenuStored,
    menuHoverBackgroundColor: menuHoverStored,
    submenuHoverBackgroundColor: submenuHoverStored,
  });
}

export function coerceNavBarColorSettings(
  a?: unknown,
  b?: unknown,
  c?: unknown,
  d?: unknown,
): StorefrontNavBarColorSettings {
  if (
    a !== null
    && typeof a === 'object'
    && !Array.isArray(a)
    && (
      'navBarBackgroundColor' in a
      || 'submenuBackgroundColor' in a
      || 'menuHoverBackgroundColor' in a
      || 'submenuHoverBackgroundColor' in a
    )
  ) {
    return a as StorefrontNavBarColorSettings;
  }
  return {
    navBarBackgroundColor: a,
    submenuBackgroundColor: b,
    menuHoverBackgroundColor: c,
    submenuHoverBackgroundColor: d,
  };
}

/** SSR-safe style attribute for the nav bar shell (empty when unset). */
export function resolveNavBarBackgroundStyleAttribute(
  settingsOrNav?: StorefrontNavBarColorSettings | unknown,
  submenuBackground?: unknown,
  menuHoverBackground?: unknown,
  submenuHoverBackground?: unknown,
): string {
  const settings = coerceNavBarColorSettings(
    settingsOrNav,
    submenuBackground,
    menuHoverBackground,
    submenuHoverBackground,
  );
  const navColor = normalizeNavBarBackgroundColor(settings.navBarBackgroundColor);
  const submenuColor = normalizeNavBarBackgroundColor(settings.submenuBackgroundColor);
  const menuHoverColor = normalizeNavBarBackgroundColor(settings.menuHoverBackgroundColor);
  const submenuHoverColor = normalizeNavBarBackgroundColor(settings.submenuHoverBackgroundColor);
  const styleParts: string[] = [];
  const dataParts: string[] = [];

  if (navColor) {
    styleParts.push(
      `${STOREFRONT_NAV_BAR_BG_VAR}:${navColor}`,
      `background-color:${navColor} !important`,
      `background:${navColor} !important`,
    );
    dataParts.push(` data-nav-bar-background="${navColor}"`);
  }

  if (submenuColor) {
    styleParts.push(`${STOREFRONT_SUBMENU_BG_VAR}:${submenuColor}`);
    dataParts.push(` data-submenu-background="${submenuColor}"`);
  }

  if (menuHoverColor) {
    styleParts.push(`${STOREFRONT_MENU_HOVER_BG_VAR}:${menuHoverColor}`);
    dataParts.push(` data-menu-hover-background="${menuHoverColor}"`);
  }

  if (submenuHoverColor) {
    styleParts.push(`${STOREFRONT_SUBMENU_HOVER_BG_VAR}:${submenuHoverColor}`);
    dataParts.push(` data-submenu-hover-background="${submenuHoverColor}"`);
  }

  if (styleParts.length === 0) return '';
  return ` style="${styleParts.join(';')}"${dataParts.join('')}`;
}

export function buildNavBarBackgroundCss(settings: StorefrontNavBarColorSettings): string {
  const navColor = normalizeNavBarBackgroundColor(settings.navBarBackgroundColor);
  const submenuColor = normalizeNavBarBackgroundColor(settings.submenuBackgroundColor);
  const menuHoverColor = normalizeNavBarBackgroundColor(settings.menuHoverBackgroundColor);
  const submenuHoverColor = normalizeNavBarBackgroundColor(settings.submenuHoverBackgroundColor);

  const rootDeclarations: string[] = [];
  if (navColor) {
    rootDeclarations.push(
      `${STOREFRONT_NAV_BAR_BG_VAR}:${navColor}`,
      `background-color:${navColor}!important`,
      `backdrop-filter:none!important`,
      `-webkit-backdrop-filter:none!important`,
    );
  }
  if (submenuColor) {
    rootDeclarations.push(`${STOREFRONT_SUBMENU_BG_VAR}:${submenuColor}`);
  }
  if (menuHoverColor) {
    rootDeclarations.push(`${STOREFRONT_MENU_HOVER_BG_VAR}:${menuHoverColor}`);
  }
  if (submenuHoverColor) {
    rootDeclarations.push(`${STOREFRONT_SUBMENU_HOVER_BG_VAR}:${submenuHoverColor}`);
  }

  const rules: string[] = [];
  if (rootDeclarations.length > 0) {
    rules.push(`[data-storefront-nav-bar="true"]{${rootDeclarations.join(';')}}`);
  }

  if (menuHoverColor) {
    rules.push(
      `[data-storefront-nav-bar="true"] .navbar-nav .nav-link:not(.nav-link-sale):hover,[data-storefront-nav-bar="true"] .navbar-nav .nav-link:not(.nav-link-sale).active{background-color:${menuHoverColor}!important}`,
    );
  }

  if (submenuHoverColor) {
    rules.push(
      `[data-storefront-nav-bar="true"] .nav-dropdown-menu .nav-dropdown-item:hover{background-color:${submenuHoverColor}!important}`,
    );
  }

  return rules.join('');
}
